import React, { useState } from 'react'
import {
    StyledLeasingListingFinanceInfo,
    StyledLeasingListingPriceInfo,
    StyledLeasingListingWithRepExampleModalPriceWrapper,
    StyledSearchListingContainer,
    StyledSearchListingDetails,
    StyledSearchListingDetailsSubTitle,
    StyledSearchListingDetailsTitle,
    StyledSearchListingLabelUL,
    StyledSearchListingPriceLabel,
    StyledSearchListingTop,
    StyledVehicleLeaseOptionPart,
} from './search-listing-details.styles'
import { StyledSearchListingLabel } from '../../patterns/search-listing/search-listing.styles'
import { ListingType } from '../../__generated__/fed-graphql-types'
import {
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_LeasingListing_finance_LeasingListingFinance,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_badges_Badge,
    SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_trackingContext_TrackingContext,
} from '../../contexts/search-results-context/query/__generated__/search-results-listings.fed.query'
import { LeasingInfoModal } from '../carousel-images/leasing-info-modal'
import { PortalComponent } from '../modals/portal'
import { focusOnElement } from '../../utils/focus-on-element'
import { useJourneyDataBuilder } from '../../hooks/use-journey-data-builder'
import { useATTrackInteraction } from '@at/consumer-web-snowplow'
import { LeasingFinanceBreakdownModalContent } from '../leasing-finance-breakdown-modal/leasing-finance-breakdown-modal-content'
import { Channel } from '@at/sauron-platform-core'
import { useChannel } from '../../hooks/use-abtest.hook'
import { useIsLeasingSearch } from '../../utils/is-leasing-search'

interface LeasingListingDetailsProps {
    advertId: string
    fpaUrl: string
    subtitle?: string | null
    title: string | null
    type?: ListingType
    badges?: ReadonlyArray<SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_badges_Badge> | null
    finance?: SearchResultsListingsGridQuery_searchResults_SearchResults_listings_LeasingListing_finance_LeasingListingFinance | null
    vatStatus?: string | null
    priceFormattedAndRounded?: string
    leasingListingRef?: HTMLDivElement
    trackingContext?: SearchResultsListingsGridQuery_searchResults_SearchResults_listings_SearchListing_trackingContext_TrackingContext
}

export const LeasingListingDetailsWithRepExampleModal = ({
    advertId,
    fpaUrl,
    subtitle,
    title,
    type,
    badges,
    finance,
    vatStatus,
    priceFormattedAndRounded,
    leasingListingRef,
    trackingContext,
}: LeasingListingDetailsProps) => {
    const [showLeasingModal, setShowLeasingModal] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const channel = useChannel()
    const isLeasingSearch = useIsLeasingSearch()
    const showVatStatus = channel === Channel.vans || isLeasingSearch

    const handleModalInteraction = useATTrackInteraction({
        trackingLabel: 'finance-info-button',
    })

    const { journeyDataBuilder } = useJourneyDataBuilder({
        trackingContext,
        ref: leasingListingRef,
        type,
        advertId,
    })

    return (
        <StyledSearchListingContainer>
            <PortalComponent show={showLeasingModal}>
                <LeasingInfoModal
                    handleHideModal={() => {
                        setShowLeasingModal(false)
                        focusOnElement(`${advertId}-leasing-info-button`)
                    }}
                />
            </PortalComponent>
            <StyledSearchListingDetails>
                <div>
                    <StyledSearchListingTop>
                        <StyledSearchListingDetailsTitle
                            as="a"
                            data-testid="leasing-listing-title"
                            href={fpaUrl}
                            onClick={journeyDataBuilder}
                        >
                            {title}
                        </StyledSearchListingDetailsTitle>

                        {subtitle && (
                            <StyledSearchListingDetailsSubTitle as="p" data-testid="search-listing-subtitle">
                                {subtitle}
                            </StyledSearchListingDetailsSubTitle>
                        )}
                    </StyledSearchListingTop>
                    {badges && (
                        <StyledSearchListingLabelUL>
                            {badges.map((badge, i) => (
                                <StyledSearchListingLabel
                                    key={`featTab-${advertId}-${i}`}
                                    as="li"
                                    $type={badge.type}
                                    $badges
                                >
                                    {badge.displayText}
                                </StyledSearchListingLabel>
                            ))}
                        </StyledSearchListingLabelUL>
                    )}
                </div>
                {finance && (
                    <StyledLeasingListingFinanceInfo as="p" data-testid="search-listing-subtitle">
                        <StyledVehicleLeaseOptionPart>{finance.initialPayment}</StyledVehicleLeaseOptionPart>
                        {' initial payment. '}
                        <StyledVehicleLeaseOptionPart>{finance.termMonths}</StyledVehicleLeaseOptionPart>
                        {' month contract. '}
                        <StyledVehicleLeaseOptionPart>{finance.mileage}</StyledVehicleLeaseOptionPart>
                        {' miles p/a. '}
                        {finance.representativeExample}
                    </StyledLeasingListingFinanceInfo>
                )}
                <StyledLeasingListingWithRepExampleModalPriceWrapper>
                    <StyledLeasingListingPriceInfo $useNewLeasingCard={true}>From&nbsp;</StyledLeasingListingPriceInfo>
                    <StyledSearchListingPriceLabel
                        as="button"
                        $isNewLeasingCard={true}
                        onClick={() => {
                            setShowModal(true)
                            return handleModalInteraction()
                        }}
                        id={`${advertId}-leasing-info-button`}
                        data-testid="monthly-price"
                    >
                        {priceFormattedAndRounded} / mo
                    </StyledSearchListingPriceLabel>
                    {showVatStatus ? (
                        <StyledLeasingListingPriceInfo $useNewLeasingCard={true}>
                            &nbsp;{vatStatus}
                        </StyledLeasingListingPriceInfo>
                    ) : null}
                </StyledLeasingListingWithRepExampleModalPriceWrapper>
                <LeasingFinanceBreakdownModalContent
                    showModal={showModal}
                    setShowModal={setShowModal}
                    representativeValues={finance?.representativeValues}
                    advertId={`${advertId}-leasing-info-button`}
                ></LeasingFinanceBreakdownModalContent>
            </StyledSearchListingDetails>
        </StyledSearchListingContainer>
    )
}
